import React from 'react';

import { Box, BoxProps, Button, Select } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { IconPlus } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { InputLabelWithInfo } from 'components/InputLabelWithInfo';
import { NoDataFound } from 'components/NoDataFound';
import { SelectItem } from 'components/SelectItem';
import { UpgradePlanBlurCard } from 'components/UpgradePlanBlurCard';
import { useSubscriptionContext } from 'contexts/SubscriptionContext';
import { InstagramIcon } from 'icons/InstagramIcon';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { IntegrationManagerService } from 'services/integration-manager-service';
import { INSTAGRAM_AVAILABLE_ACCOUNTS_QUERY_KEY } from 'shared/constants/query-keys';
import { ROUTE_LINKS } from 'shared/constants/route-links';
import { IntegrationType } from 'shared/enums/integration';
import { InstagramAccountHighLevelPermissionType } from 'shared/interfaces/InstagramAccount';
import { InstagramIntegrationFormValues, SmartAssistant } from 'shared/interfaces/SmartAssistant';

interface InstagramIntegrationSectionProps extends BoxProps {
  form: UseFormReturnType<InstagramIntegrationFormValues>;
  smartAssistant: SmartAssistant;
}

export function InstagramIntegrationSection({ form, smartAssistant, ...boxProps }: InstagramIntegrationSectionProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [subscription] = useSubscriptionContext();

  const canIntegrate = subscription.product.enableInstagram;

  const getAvailableInstagramAccountsQuery = useQuery({
    queryKey: [INSTAGRAM_AVAILABLE_ACCOUNTS_QUERY_KEY, { smartAssistantId: smartAssistant._id }],
    queryFn: () =>
      IntegrationManagerService.getAvailableInstagramAccounts(
        smartAssistant._id,
        InstagramAccountHighLevelPermissionType.MESSAGING
      ),
    enabled: !!canIntegrate
  });

  if (getAvailableInstagramAccountsQuery.isLoading) return null;
  if (getAvailableInstagramAccountsQuery.isError || !getAvailableInstagramAccountsQuery.data) return null;

  const availableInstagramAccounts = getAvailableInstagramAccountsQuery.data;

  const availableInstagramAccountsItems = availableInstagramAccounts.map((account) => ({
    icon: <InstagramIcon />,
    value: account._id,
    label: account.username
  }));

  const onAccountChange = (value: string) => {
    form.setFieldValue('instagramAccount', value);
  };

  return (
    <UpgradePlanBlurCard
      message={t('SMART_ASSISTANT.INSTAGRAM_INTEGRATION_SECTION.UPGRADE.DESCRIPTION')}
      showCard={!canIntegrate}>
      <Box {...boxProps}>
        {availableInstagramAccounts.length > 0 ? (
          <Select
            label={
              <InputLabelWithInfo
                label={t('SMART_ASSISTANT.INSTAGRAM.ACCOUNT.LABEL')}
                infoContentTranslationKey="SMART_ASSISTANT.INSTAGRAM.ACCOUNT.INFO"
              />
            }
            itemComponent={SelectItem}
            data={availableInstagramAccountsItems}
            error={form.errors.instagramAccount}
            onChange={(value) => value && onAccountChange(value)}
            value={form.values.instagramAccount}
          />
        ) : (
          <NoDataFound
            h="100%"
            size="xs"
            icon={<InstagramIcon />}
            title={t('SMART_ASSISTANT.INSTAGRAM.NO_ACCOUNT_FOUND.TITLE')}
            subTitle={t('SMART_ASSISTANT.INSTAGRAM.NO_ACCOUNT_FOUND.SUB_TITLE')}
            button={
              <Button
                leftIcon={<IconPlus style={{ width: 18, height: 18 }} />}
                onClick={() => navigate(`${ROUTE_LINKS.integrations}?type=${IntegrationType.INSTAGRAM}`)}
                size="xs">
                {t('SMART_ASSISTANT.INSTAGRAM.NO_ACCOUNT_FOUND.CONNECT_ACCOUNT')}
              </Button>
            }
          />
        )}
      </Box>
    </UpgradePlanBlurCard>
  );
}
